import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import superAdminApi from "../../../lib/superAdminApi";
import SuperAdminLayout from "../../../components/SuperAdminLayout";
import { ArrowLeft, Pencil, X } from "lucide-react";
import { toast } from "react-toastify";

interface PackageDetails {
  [key: string]: any;
}

interface BusinessDetail {
  id: string;
  name?: string | null;
  website?: string | null;
  description?: string | null;
  logo?: string | null;
  created_at?: string | null;
  User: {
    id: string;
    email: string;
    firstname?: string | null;
    lastname?: string | null;
    is_active: boolean;
    Role?: { name?: string | null } | null;
  }[];
  Store: {
    id: string;
    name?: string | null;
    location?: string | null;
    contact_info?: string | null;
    created_at?: string | null;
    _count: { Product: number; Sale: number };
  }[];
  BusinessPackageSubscription?: {
    id: string;
    is_active?: boolean | null;
    billing_cycle?: string | null;
    auto_renew?: boolean | null;
    start_date?: string | null;
    end_date?: string | null;
    Package?: {
      id: string;
      name?: string | null;
      PackageDetails?: PackageDetails[];
      PackageBilling?: PackageDetails[];
    } | null;
    FreeTrial?: PackageDetails[];
  } | null;
  SubscriptionPayment: {
    id: string;
    amount?: any;
    status?: string | null;
    created_at?: string | null;
    Package?: { name?: string | null } | null;
  }[];
}

interface EditForm {
  name: string;
  website: string;
  description: string;
}

const statusColors: Record<string, string> = {
  paid: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  failed: "bg-red-100 text-red-600",
  refunded: "bg-gray-100 text-gray-600",
};

const SuperAdminBusinessDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<BusinessDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({ name: "", website: "", description: "" });
  const [saving, setSaving] = useState(false);

  const fetchBusiness = () => {
    setLoading(true);
    superAdminApi
      .get(`/superadmin/businesses/${id}`)
      .then((res) => setBusiness(res.data.business))
      .catch((err) => toast.error(err.response?.data?.message ?? "Failed to load business"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (id) fetchBusiness();
  }, [id]);

  const openEditModal = () => {
    if (!business) return;
    setEditForm({
      name: business.name ?? "",
      website: business.website ?? "",
      description: business.description ?? "",
    });
    setShowEditModal(true);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!business) return;
    setSaving(true);
    try {
      await superAdminApi.put(`/superadmin/businesses/${business.id}`, editForm);
      toast.success("Business updated successfully");
      setShowEditModal(false);
      fetchBusiness();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to update business");
    } finally {
      setSaving(false);
    }
  };

  const formatAmount = (amount: any) => {
    if (amount == null) return "—";
    const num = parseFloat(amount);
    if (isNaN(num)) return String(amount);
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const paymentStatusClass = (status?: string | null) =>
    status ? (statusColors[status.toLowerCase()] ?? "bg-gray-100 text-gray-600") : "bg-gray-100 text-gray-600";

  return (
    <SuperAdminLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/superadmin/businesses")}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Business Detail</h1>
              <p className="text-gray-500 text-sm mt-0.5">View and manage business information</p>
            </div>
          </div>
          {business && (
            <button
              onClick={openEditModal}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
        </div>

        {loading ? (
          <div className="space-y-5 animate-pulse">
            {/* Business info skeleton */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="h-4 bg-gray-200 rounded w-44 mb-5" />
              <div className="flex gap-4">
                <div className="w-16 h-16 rounded-lg bg-gray-200 flex-shrink-0" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i}>
                      <div className="h-3 bg-gray-200 rounded w-16 mb-1.5" />
                      <div className="h-4 bg-gray-200 rounded w-32" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Subscription skeleton */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="h-4 bg-gray-200 rounded w-40 mb-5" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i}>
                    <div className="h-3 bg-gray-200 rounded w-20 mb-1.5" />
                    <div className="h-4 bg-gray-200 rounded w-24" />
                  </div>
                ))}
              </div>
            </div>
            {/* Users table skeleton */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <div className="h-4 bg-gray-200 rounded w-20" />
              </div>
              <div className="p-4 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-4 bg-gray-200 rounded w-32" />
                    <div className="h-4 bg-gray-200 rounded w-40" />
                    <div className="h-5 bg-gray-200 rounded-full w-16" />
                    <div className="h-5 bg-gray-200 rounded-full w-14" />
                  </div>
                ))}
              </div>
            </div>
            {/* Stores table skeleton */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <div className="h-4 bg-gray-200 rounded w-20" />
              </div>
              <div className="p-4 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-4 bg-gray-200 rounded w-32" />
                    <div className="h-4 bg-gray-200 rounded w-28" />
                    <div className="h-4 bg-gray-200 rounded w-8" />
                    <div className="h-4 bg-gray-200 rounded w-8" />
                  </div>
                ))}
              </div>
            </div>
            {/* Payments skeleton */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <div className="h-4 bg-gray-200 rounded w-32" />
              </div>
              <div className="p-4 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-4 bg-gray-200 rounded w-20" />
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-4 bg-gray-200 rounded w-16" />
                    <div className="h-5 bg-gray-200 rounded-full w-14" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : !business ? (
          <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Business not found.</div>
        ) : (
          <div className="space-y-5">
            {/* Business info card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Business Information</h2>
              <div className="flex gap-4 items-start">
                {business.logo && (
                  <img
                    src={business.logo}
                    alt="Business logo"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                  />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Name</p>
                    <p className="text-sm font-medium text-gray-900">{business.name ?? "Unnamed"}</p>
                  </div>
                  {business.website && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Website</p>
                      <a
                        href={business.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-indigo-600 hover:underline"
                      >
                        {business.website}
                      </a>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Created</p>
                    <p className="text-sm text-gray-900">
                      {business.created_at ? new Date(business.created_at).toLocaleDateString() : "—"}
                    </p>
                  </div>
                  {business.description && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-gray-400 mb-0.5">Description</p>
                      <p className="text-sm text-gray-600">{business.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Subscription info */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Active Subscription</h2>
              {business.BusinessPackageSubscription ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Package</p>
                    <p className="text-sm text-gray-900">
                      {business.BusinessPackageSubscription.Package?.name ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Billing Cycle</p>
                    <p className="text-sm text-gray-900 capitalize">
                      {business.BusinessPackageSubscription.billing_cycle ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Status</p>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        business.BusinessPackageSubscription.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-600"
                      }`}
                    >
                      {business.BusinessPackageSubscription.is_active ? "Active" : "Expired"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Start Date</p>
                    <p className="text-sm text-gray-900">
                      {business.BusinessPackageSubscription.start_date
                        ? new Date(business.BusinessPackageSubscription.start_date).toLocaleDateString()
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">End Date</p>
                    <p className="text-sm text-gray-900">
                      {business.BusinessPackageSubscription.end_date
                        ? new Date(business.BusinessPackageSubscription.end_date).toLocaleDateString()
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Auto-Renew</p>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        business.BusinessPackageSubscription.auto_renew
                          ? "bg-indigo-100 text-indigo-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {business.BusinessPackageSubscription.auto_renew ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No active subscription.</p>
              )}
            </div>

            {/* Users table */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h2 className="text-sm font-semibold text-gray-700">
                  Users{" "}
                  <span className="text-gray-400 font-normal">({business.User.length})</span>
                </h2>
              </div>
              {business.User.length === 0 ? (
                <p className="px-5 py-4 text-sm text-gray-400">No users.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr className="text-left text-gray-500">
                        <th className="px-4 py-3 font-medium">Name</th>
                        <th className="px-4 py-3 font-medium">Email</th>
                        <th className="px-4 py-3 font-medium">Role</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {business.User.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <button
                              onClick={() => navigate(`/superadmin/users/${u.id}`)}
                              className="text-left hover:text-indigo-600 transition-colors"
                            >
                              <p className="font-medium text-gray-900">
                                {[u.firstname, u.lastname].filter(Boolean).join(" ") || "—"}
                              </p>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-gray-500">{u.email}</td>
                          <td className="px-4 py-3">
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              {u.Role?.name ?? "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                u.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                              }`}
                            >
                              {u.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Stores table */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h2 className="text-sm font-semibold text-gray-700">
                  Stores{" "}
                  <span className="text-gray-400 font-normal">({business.Store.length})</span>
                </h2>
              </div>
              {business.Store.length === 0 ? (
                <p className="px-5 py-4 text-sm text-gray-400">No stores.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr className="text-left text-gray-500">
                        <th className="px-4 py-3 font-medium">Store</th>
                        <th className="px-4 py-3 font-medium">Location</th>
                        <th className="px-4 py-3 font-medium">Products</th>
                        <th className="px-4 py-3 font-medium">Sales</th>
                        <th className="px-4 py-3 font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {business.Store.map((store) => (
                        <tr key={store.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900">{store.name ?? "—"}</td>
                          <td className="px-4 py-3 text-gray-500">{store.location ?? "—"}</td>
                          <td className="px-4 py-3 text-gray-600">{store._count.Product}</td>
                          <td className="px-4 py-3 text-gray-600">{store._count.Sale}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">
                            {store.created_at ? new Date(store.created_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Payment history */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h2 className="text-sm font-semibold text-gray-700">Payment History</h2>
              </div>
              {business.SubscriptionPayment.length === 0 ? (
                <p className="px-5 py-4 text-sm text-gray-400">No payment history.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr className="text-left text-gray-500">
                        <th className="px-4 py-3 font-medium">Date</th>
                        <th className="px-4 py-3 font-medium">Package</th>
                        <th className="px-4 py-3 font-medium">Amount</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {business.SubscriptionPayment.slice(0, 10).map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-gray-400 text-xs">
                            {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{payment.Package?.name ?? "—"}</td>
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {formatAmount(payment.amount)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${paymentStatusClass(
                                payment.status
                              )}`}
                            >
                              {payment.status ?? "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">Edit Business</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleEditSave} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Business Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
                <input
                  type="text"
                  value={editForm.website}
                  onChange={(e) => setEditForm((f) => ({ ...f, website: e.target.value }))}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminBusinessDetail;
